import React, { createContext, useState, useContext, useCallback, useEffect, useRef } from 'react';
import axios from 'axios';
import { AuthContext } from './AuthContext';

const ContatosContext = createContext();

// const version = 'v1';
const version = '';
const contatoDataAtual = version + 'contatoData';
const currentPageAtual = version + 'paginaContato';
const cursorContatoAtual = version + 'cursorContato';

// const manterItens = [contatoDataAtual, currentPageAtual, 'fetchDate'];
// Object.keys(localStorage).forEach((chave) => {
//     if (!manterItens.includes(chave)) {
//         localStorage.removeItem(chave);
//     }
// });

const paraOpcaoContato = (result) => ({
    value: result.id,
    label: result.text,
    unidadeContato: result.unidadeContato,
    aniversario: result.aniversario !== "NaN/NaN/NaN" ? result.aniversario : "",
    phone: result.phone || "",
    diacono: result.diacono || "",
    dataInicioDiacono: result.dataInicioDiacono || ""
});

export const useContatos = () => {
    return useContext(ContatosContext);
};

export const ContatosProvider = ({ children }) => {

    const [contatoData, setContatoData] = useState(() => {
        const savedData = localStorage.getItem(contatoDataAtual);
        return savedData ? JSON.parse(savedData) : [];
    });

    const [isLoading, setIsLoading] = useState(false);
    const isLoadingRef = useRef(isLoading);

    const [currentPage, setCurrentPage] = useState(() => {
        const savedPage = localStorage.getItem(currentPageAtual);
        return savedPage ? Number(savedPage) : 0;
    });
    const currentPageRef = useRef(currentPage);
    // Último ID recebido: a API usa como cursor da próxima página (evita a contagem lenta do Bitrix)
    const cursorContatoRef = useRef(localStorage.getItem(cursorContatoAtual) || null);
    const { userAcesso, userInfo } = useContext(AuthContext);


    useEffect(() => {
        isLoadingRef.current = isLoading;
        currentPageRef.current = currentPage;
    }, [isLoading, currentPage, contatoData]);

    const urlApi = process.env.REACT_APP_URL_API || 'https://return-navy.vercel.app/';

    // Sem acesso total, a API só devolve contatos das unidades do usuário. null = sem unidade vinculada
    const montaParamsBase = useCallback(() => {
        if (!userInfo?.bitrix || (userInfo.bitrix.unidadesLista === 0 && !userInfo.bitrix.acessoTotal)) {
            return null;
        }
        const userUnidadeId = userInfo.bitrix.unidadesLista;
        return (userUnidadeId && !userInfo.bitrix.acessoTotal) ? { unidadeId: userUnidadeId } : {};
    }, [userInfo]);

    // Lista padrão (sem pesquisa): carrega a próxima página e acumula em contatoData
    const buscaContatos = useCallback(async () => {
        // Limite de requisição
        if (isLoadingRef.current || currentPageRef.current >= 4) { return }

        const paramsBase = montaParamsBase();
        if (!paramsBase) {
            console.error('Não há unidade vinculada a sua conta');
            return;
        }

        isLoadingRef.current = true;
        setIsLoading(true);
        try {
            const params = {
                ...paramsBase,
                page: currentPageRef.current + 1,
                ...(cursorContatoRef.current && { afterId: cursorContatoRef.current })
            };
            setCurrentPage((prevPage) => {
                const newPage = prevPage + 1;
                localStorage.setItem(currentPageAtual, newPage);
                return newPage;
            });

            const response = await axios.get(urlApi + 'v1/contatos', { params });

            cursorContatoRef.current = response.data.nextCursor || null;
            if (cursorContatoRef.current) {
                localStorage.setItem(cursorContatoAtual, cursorContatoRef.current);
            } else {
                localStorage.removeItem(cursorContatoAtual);
            }
            setContatoData((prevData) => [...prevData, ...response.data.results.map(paraOpcaoContato)]);

            // Save fetch date if it's the first fetch
            if (!localStorage.getItem('fetchDate')) {
                localStorage.setItem('fetchDate', new Date().toISOString());
            }
        } catch (error) {
            console.error('Erro buscaContato - ContatosContext');
        } finally {
            isLoadingRef.current = false;
            setIsLoading(false);
        }
    }, [montaParamsBase, urlApi]);

    // Pesquisa por nome: separada da paginação acima, para nunca ser descartada enquanto uma página carrega
    const pesquisaContatos = useCallback(async (term, signal) => {
        const paramsBase = montaParamsBase();
        if (!paramsBase) return [];
        const response = await axios.get(urlApi + 'v1/contatos', { params: { ...paramsBase, page: 1, term }, signal });
        return response.data.results.map(paraOpcaoContato);
    }, [montaParamsBase, urlApi]);

    useEffect(() => {
        if (contatoData.length !== 0) {
            localStorage.setItem(contatoDataAtual, JSON.stringify(contatoData));
        }
    }, [contatoData]);

    const checkAndClearLocalStorage = () => {
        const savedDate = localStorage.getItem('fetchDate');
        if (savedDate) {
            const savedDateObj = new Date(savedDate);
            const currentDate = new Date();
            const timeDiff = currentDate - savedDateObj;
            const daysDiff = timeDiff / (1000 * 60 * 60 * 24); // Converte ms em dias
            if (daysDiff > 7) { // 7 dias // tempo de expiração do localStorage
                apagaTudo();
            }
        }
    };

    // Carga inicial: espera as unidades do usuário chegarem (o efeito roda de novo a cada mudança no userInfo)
    // e dispara a busca uma única vez, sem setInterval
    const carregamentoInicialRef = useRef(false);
    useEffect(() => {
        checkAndClearLocalStorage();
        if (!userAcesso) {
            carregamentoInicialRef.current = false;
            return;
        }
        if (
            contatoData.length === 0 &&
            userInfo?.bitrix?.acessoTotal !== undefined &&
            userInfo?.bitrix?.unidades &&
            !carregamentoInicialRef.current &&
            !isLoadingRef.current
        ) {
            carregamentoInicialRef.current = true;
            buscaContatos();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userAcesso, userInfo]);

    const apagaTudo = () => {
        carregamentoInicialRef.current = false;
        cursorContatoRef.current = null;
        setContatoData([]);
        setCurrentPage(1)
        localStorage.clear();
    };

    return (
        <ContatosContext.Provider value={{ isLoading, currentPage, contatoData, buscaContatos, pesquisaContatos }}>
            {children}
        </ContatosContext.Provider>
    );
};

// Estado de um seletor de contatos: espera o usuário parar de digitar, cancela a pesquisa anterior e
// mostra "Carregando..." enquanto pesquisa, em vez de deixar a lista padrão na tela
export const useBuscaContatos = () => {
    const { contatoData, buscaContatos, pesquisaContatos, isLoading } = useContatos();
    const [resultados, setResultados] = useState(undefined); // undefined = sem pesquisa, mostra a lista padrão
    const [pesquisando, setPesquisando] = useState(false);
    const timeoutRef = useRef(null);
    const controllerRef = useRef(null);

    const cancelaPesquisa = useCallback(() => {
        clearTimeout(timeoutRef.current);
        controllerRef.current?.abort();
    }, []);

    useEffect(() => cancelaPesquisa, [cancelaPesquisa]);

    const onSearch = (term) => {
        cancelaPesquisa();
        const termo = String(term || '').trim();
        if (termo.length < 3) {
            setResultados(undefined);
            setPesquisando(false);
            return;
        }
        setResultados([]);
        setPesquisando(true);
        timeoutRef.current = setTimeout(async () => {
            const controller = new AbortController();
            controllerRef.current = controller;
            try {
                const encontrados = await pesquisaContatos(termo, controller.signal);
                if (controller.signal.aborted) return;
                setResultados(encontrados);
            } catch (error) {
                if (controller.signal.aborted || axios.isCancel(error)) return;
                console.error('Erro ao pesquisar contatos - ContatosContext');
                setResultados([]);
            }
            setPesquisando(false);
        }, 400);
    };

    return {
        opcoes: resultados === undefined ? contatoData : resultados,
        isLoading: resultados === undefined ? isLoading : pesquisando,
        onSearch,
        onLoadMore: resultados === undefined ? buscaContatos : undefined,
    };
};
